// src/features/operational/components/subject/SubjectPlanning.js
import React, { useState, useEffect } from 'react';
import {
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Button, 
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Snackbar,
  Alert,
  Paper
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  ContentPaste as PasteIcon,
  CalendarMonth as CalendarIcon,
  Assessment as AssessmentIcon,
  School as SchoolIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudDownload as DownloadIcon,
  CloudUpload as UploadIcon,
  Print as PrintIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  fetchUserSubjects,
  addSubject,
  updateSubject,
  deleteSubject
} from '../../services/subjectService';

const SubjectPlanning = ({ userId }) => {
  const { userProfile } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [subjectPlans, setSubjectPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuPlanId, setMenuPlanId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialogs
  const [isAddSubjectDialogOpen, setIsAddSubjectDialogOpen] = useState(false);
  const [isEditSubjectDialogOpen, setIsEditSubjectDialogOpen] = useState(false);
  const [isAddPlanDialogOpen, setIsAddPlanDialogOpen] = useState(false);
  const [isEditPlanDialogOpen, setIsEditPlanDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  
  // Form data
  const [newSubjectData, setNewSubjectData] = useState({
    name: '',
    code: '',
    grade: '',
    description: '',
    department: '',
    phase: '',
    status: 'Active',
    policyDocument: null
  });
  
  const [editSubjectData, setEditSubjectData] = useState({
    id: '',
    name: '',
    code: '',
    grade: '',
    description: '',
    department: '',
    phase: '',
    status: 'Active'
  });
  
  const [newPlanData, setNewPlanData] = useState({
    name: '',
    type: '',
    description: '',
    dueDate: '',
    documents: []
  });
  
  const [editPlanData, setEditPlanData] = useState({
    id: '',
    name: '',
    type: '',
    description: '',
    dueDate: '',
    status: '',
    documents: []
  });
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadPlanId, setUploadPlanId] = useState(null);
  
  // Notifications
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const mockPlans = [
    { id: '1', name: 'Annual Teaching Plan', type: 'atp', description: 'ATP for Term 1-4', dueDate: '2025-05-01', status: 'in-progress' },
    { id: '2', name: 'Subject Improvement Plan', type: 'sip', description: 'Strategies to improve pass rate', dueDate: '2025-04-30', status: 'not-started' },
    { id: '3', name: 'Programme of Assessment', type: 'poa', description: 'Assessment schedule for the year', dueDate: '2025-04-15', status: 'completed', documentUrl: '#' },
    { id: '4', name: 'Remedial Action Plan', type: 'other', description: 'Plan for struggling learners', dueDate: '2025-05-20', status: 'not-started' }
  ];
  
  useEffect(() => {
    const loadSubjects = async () => {
      if (!userId) {
        setSubjects([]);
        setSelectedSubject(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const subjectData = await fetchUserSubjects(userId);
        setSubjects(subjectData);
        setSelectedSubject((prevSelected) => {
          if (!subjectData.length) {
            return null;
          }

          if (prevSelected) {
            return subjectData.find((subject) => subject.id === prevSelected.id) || subjectData[0];
          }

          return subjectData[0];
        });
      } catch (error) {
        console.error('Error loading subjects:', error);
        showNotification('Failed to load subjects', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadSubjects();
  }, [userId]);
  
  useEffect(() => {
    if (selectedSubject) {
      setPlansLoading(true);
      // Simulating API call
      setTimeout(() => {
        setSubjectPlans(mockPlans);
        setPlansLoading(false);
      }, 600);
    }
  }, [selectedSubject]);
  
  const filteredSubjects = subjects.filter(subject => {
    const matchesSearch = !searchTerm || 
      subject.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subject.code.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPhase = !phaseFilter || subject.phase === phaseFilter;
    
    return matchesSearch && matchesPhase;
  });
  
  const handleMenuClick = (event, planId) => {
    setAnchorEl(event.currentTarget);
    setMenuPlanId(planId);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPlanId(null);
  };
  
  const handleSubjectChange = (subject) => {
    setSelectedSubject(subject);
  };
  
  // Add Subject Dialog
  const handleAddSubjectDialogOpen = () => {
    setNewSubjectData({
      name: '',
      code: '',
      grade: '',
      description: '',
      department: '',
      phase: '',
      status: 'Active',
      policyDocument: null
    });
    setIsAddSubjectDialogOpen(true);
  };
  
  const handleAddSubjectDialogClose = () => {
    setIsAddSubjectDialogOpen(false);
  };
  
  const handleNewSubjectChange = (e) => {
    const { name, value } = e.target;
    setNewSubjectData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePolicyDocumentChange = (e) => {
    const file = e.target.files?.[0] || null;
    setNewSubjectData(prev => ({
      ...prev,
      policyDocument: file
    }));
  };
  
  const handleAddSubject = async () => {
    if (!userId) {
      showNotification("You must be signed in to add a subject", "error");
      return;
    }

    try {
      const createdSubject = await addSubject(userId, {
        ...newSubjectData,
        institutionId: userProfile?.institutionId || '',
        institutionName: userProfile?.institutionName || '',
        createdBy: userId
      });

      setSubjects(prev => [createdSubject, ...prev]);
      setSelectedSubject(createdSubject);
      showNotification("Subject added successfully", "success");
      handleAddSubjectDialogClose();
    } catch (error) {
      console.error('Error adding subject:', error);
      showNotification("Failed to add subject", "error");
    }
  };
  
  // Edit Subject Dialog
  const handleEditSubjectDialogOpen = (subject) => {
    setEditSubjectData({
      id: subject.id,
      name: subject.name,
      code: subject.code || '',
      grade: subject.grade,
      description: subject.description || '',
      department: subject.department || '',
      phase: subject.phase || '',
      status: subject.status || 'Active'
    });
    setIsEditSubjectDialogOpen(true);
  };
  
  const handleEditSubjectDialogClose = () => {
    setIsEditSubjectDialogOpen(false);
  };
  
  const handleEditSubjectChange = (e) => {
    const { name, value } = e.target;
    setEditSubjectData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleUpdateSubject = async () => {
    try {
      const { id, ...subjectData } = editSubjectData;
      const updatedSubject = await updateSubject(id, subjectData);

      setSubjects(prev =>
        prev.map(subject => (subject.id === id ? updatedSubject : subject))
      );

      if (selectedSubject?.id === id) {
        setSelectedSubject(updatedSubject);
      }

      showNotification("Subject updated successfully", "success");
      handleEditSubjectDialogClose();
    } catch (error) {
      console.error('Error updating subject:', error);
      showNotification("Failed to update subject", "error");
    }
  };
  
  const handleDeleteSubject = async (subjectId) => {
    // Confirm before deleting
    if (!window.confirm("Are you sure you want to delete this subject? This action cannot be undone.")) {
      return;
    }
    
    try {
      await deleteSubject(subjectId);

      const updatedSubjects = subjects.filter(subject => subject.id !== subjectId);
      setSubjects(updatedSubjects);

      if (selectedSubject?.id === subjectId) {
        setSelectedSubject(updatedSubjects.length > 0 ? updatedSubjects[0] : null);
      }

      showNotification("Subject deleted successfully", "success");
      handleEditSubjectDialogClose();
    } catch (error) {
      console.error('Error deleting subject:', error);
      showNotification("Failed to delete subject", "error");
    }
  };
  
  // Add Plan Dialog
  const handleAddPlanDialogOpen = () => {
    setNewPlanData({
      name: '',
      type: '',
      description: '',
      dueDate: '',
      documents: []
    });
    setIsAddPlanDialogOpen(true);
  };
  
  const handleAddPlanDialogClose = () => {
    setIsAddPlanDialogOpen(false);
  };
  
  const handleNewPlanChange = (e) => {
    const { name, value } = e.target;
    setNewPlanData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNewPlanFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const newDocs = files.map(file => ({
      name: file.name,
      size: file.size,
      url: URL.createObjectURL(file),
      file
    }));
    setNewPlanData(prev => ({
      ...prev,
      documents: [...prev.documents, ...newDocs]
    }));
    e.target.value = '';
  };

  const handleRemoveNewPlanDoc = (index) => {
    setNewPlanData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };
  
  const handleAddPlan = async () => {
    if (!selectedSubject) return;
    
    // Simulate adding plan
    const newPlan = {
      id: `plan_${Date.now()}`,
      ...newPlanData,
      status: 'not-started',
      createdAt: new Date().toISOString()
    };
    
    setSubjectPlans(prev => [...prev, newPlan]);
    showNotification("Plan added successfully", "success");
    handleAddPlanDialogClose();
  };
  
  // Edit Plan Dialog
  const handleEditPlanDialogOpen = (plan) => {
    setEditPlanData({
      id: plan.id,
      name: plan.name,
      type: plan.type,
      description: plan.description || '',
      dueDate: plan.dueDate || '',
      status: plan.status || 'not-started',
      documents: plan.documents || (plan.documentUrl ? [{
        name: plan.documentName || 'Document',
        size: plan.documentSize || 0,
        url: plan.documentUrl
      }] : [])
    });
    setIsEditPlanDialogOpen(true);
  };
  
  const handleEditPlanDialogClose = () => {
    setIsEditPlanDialogOpen(false);
  };
  
  const handleEditPlanChange = (e) => {
    const { name, value } = e.target;
    setEditPlanData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditPlanFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const newDocs = files.map(file => ({
      name: file.name,
      size: file.size,
      url: URL.createObjectURL(file),
      file
    }));
    setEditPlanData(prev => ({
      ...prev,
      documents: [...prev.documents, ...newDocs]
    }));
    e.target.value = '';
  };

  const handleRemoveEditPlanDoc = (index) => {
    setEditPlanData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };
  
  const handleUpdatePlan = async () => {
    if (!selectedSubject) return;
    
    // Simulate updating plan
    const { id, ...planData } = editPlanData;
    setSubjectPlans(prev => 
      prev.map(plan => plan.id === id ? { ...plan, ...planData } : plan)
    );
    
    showNotification("Plan updated successfully", "success");
    handleEditPlanDialogClose();
  };
  
  const handleDeletePlan = async (planId) => {
    if (!selectedSubject) return;
    
    // Confirm before deleting
    if (!window.confirm("Are you sure you want to delete this plan? This action cannot be undone.")) {
      handleMenuClose();
      return;
    }
    
    // Simulate deleting plan
    setSubjectPlans(prev => prev.filter(plan => plan.id !== planId));
    showNotification("Plan deleted successfully", "success");
    
    handleMenuClose();
  };
  
  // Upload Document Dialog
  const handleUploadDialogOpen = (planId) => {
    setUploadPlanId(planId);
    setSelectedFile(null);
    setIsUploadDialogOpen(true);
    handleMenuClose();
  };
  
  const handleUploadDialogClose = () => {
    setIsUploadDialogOpen(false);
    setUploadPlanId(null);
    setSelectedFile(null);
  };
  
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };
  
  const handleUploadDocument = async () => {
    if (!selectedSubject || !uploadPlanId || !selectedFile) return;
    
    // Simulate uploading document
    setSubjectPlans(prev => 
      prev.map(plan => plan.id === uploadPlanId ? {
        ...plan,
        documentUrl: URL.createObjectURL(selectedFile),
        documentName: selectedFile.name
      } : plan)
    );
    
    showNotification("Document uploaded successfully", "success");
    handleUploadDialogClose();
  };
  
  // Notifications
  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };
  
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };
  
  const getStatusChip = (status) => {
    switch (status) {
      case 'completed':
        return <Chip size="small" label="Completed" sx={{ bgcolor: '#4caf50', color: 'white' }} />;
      case 'in-progress':
        return <Chip size="small" label="In Progress" sx={{ bgcolor: '#ff9800', color: 'white' }} />;
      case 'not-started':
        return <Chip size="small" label="Not Started" sx={{ bgcolor: '#f44336', color: 'white' }} />;
      default:
        return <Chip size="small" label="Unknown" />;
    }
  };
  
  const getIconForPlanType = (type) => {
    switch (type) {
      case 'sip':
        return <PasteIcon sx={{ color: THEME_COLORS.primary }} />;
      case 'atp':
        return <CalendarIcon sx={{ color: '#4caf50' }} />;
      case 'poa':
        return <AssessmentIcon sx={{ color: '#f44336' }} />;
      case 'lessonPlan':
        return <SchoolIcon sx={{ color: '#2196f3' }} />;
      default:
        return <DescriptionIcon sx={{ color: '#9e9e9e' }} />;
    }
  };
  
  const phases = [
    { value: '', label: 'All Phases' },
    { value: 'Foundation', label: 'Foundation Phase' },
    { value: 'Intermediate', label: 'Intermediate Phase' },
    { value: 'Senior', label: 'Senior Phase' },
    { value: 'FET', label: 'FET Phase' }
  ];
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress sx={{ color: THEME_COLORS.primary }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with search and filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold" color={THEME_COLORS.primary}>
            Subject Planning
          </Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddSubjectDialogOpen}
              sx={{
                backgroundColor: THEME_COLORS.primary,
                '&:hover': { backgroundColor: '#1e1c4e' }
              }}
            >
              Add Subject
            </Button>
          </Box>
        </Box>
        
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
          <Box sx={{ position: 'relative', flexGrow: 1 }}>
            <SearchIcon sx={{ position: 'absolute', left: 1, top: '50%', transform: 'translateY(-50%)', ml: 1, color: 'text.secondary' }} />
            <TextField 
              fullWidth
              placeholder="Search subjects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  paddingLeft: '35px'
                }
              }}
            />
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                borderColor: THEME_COLORS.primary,
                color: THEME_COLORS.primary,
                '&:hover': {
                  borderColor: '#1e1c4e',
                  backgroundColor: 'rgba(39, 35, 97, 0.04)'
                }
              }}
            >
              Filters
            </Button>
          </Box>
        </Box>
        
        {showFilters && (
          <Box mt={2} pt={2} borderTop="1px solid #e0e0e0">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Phase</InputLabel>
                  <Select
                    value={phaseFilter}
                    onChange={(e) => setPhaseFilter(e.target.value)}
                    label="Phase"
                  >
                    {phases.map(phase => (
                      <MenuItem key={phase.value} value={phase.value}>{phase.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8} display="flex" justifyContent="flex-end" alignItems="center">
                <Button 
                  variant="text"
                  onClick={() => {
                    setSearchTerm('');
                    setPhaseFilter('');
                  }}
                  sx={{ color: THEME_COLORS.primary }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
      
      <Grid container spacing={2}>
        {/* Plans List */}
        <Grid item sx={{ width: { xs: '100%', md: '65%' } }}>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  {selectedSubject ? 
                    `${selectedSubject.name} - Grade ${selectedSubject.grade} Plans` : 
                    'Subject Plans'}
                </Typography>
                {selectedSubject && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddPlanDialogOpen}
                    sx={{
                      bgcolor: THEME_COLORS.primary,
                      '&:hover': {
                        bgcolor: '#1e1c4e'
                      }
                    }}
                  >
                    Add Plan
                  </Button>
                )}
              </Box>
              
              {selectedSubject && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedSubject.description || 'No description provided'}
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                    <Chip 
                      label={`Phase: ${selectedSubject.phase}`} 
                      size="small" 
                      sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }}
                    />
                    {selectedSubject.department && (
                      <Chip 
                        label={`Department: ${selectedSubject.department}`} 
                        size="small"
                        sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }}
                      />
                    )}
                  </Box>
                </Box>
              )}
              
              <Divider sx={{ mb: 2 }} />
              
              {!selectedSubject ? (
                <Box py={4} textAlign="center">
                  <Typography color="textSecondary">
                    Please select a subject to view plans
                  </Typography>
                </Box>
              ) : plansLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                  <CircularProgress sx={{ color: THEME_COLORS.primary }} />
                </Box>
              ) : subjectPlans.length === 0 ? (
                <Box py={4} textAlign="center">
                  <Typography color="textSecondary" mb={2}>
                    No plans found for this subject
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddPlanDialogOpen}
                    sx={{
                      bgcolor: THEME_COLORS.primary,
                      '&:hover': {
                        bgcolor: '#1e1c4e'
                      }
                    }}
                  >
                    Add Your First Plan
                  </Button>
                </Box>
              ) : (
                <List>
                  {subjectPlans.map((plan) => (
                    <Paper
                      key={plan.id}
                      elevation={1}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: 3
                        }
                      }}
                    >
                      <ListItem
                        sx={{
                          borderRadius: 2,
                          py: 2
                        }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            onClick={(e) => handleMenuClick(e, plan.id)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        }
                      >
                        <ListItemIcon>
                          {getIconForPlanType(plan.type)}
                        </ListItemIcon>
                        <ListItemText
                          primaryTypographyProps={{ component: 'div' }}
                          secondaryTypographyProps={{ component: 'div' }}
                          primary={
                            <Box display="flex" alignItems="center">
                              <Typography fontWeight="medium" mr={1}>
                                {plan.name}
                              </Typography>
                              {getStatusChip(plan.status)}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {plan.description || 'No description'}
                              </Typography>
                              {plan.dueDate && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  <strong>Due:</strong> {plan.dueDate}
                                </Typography>
                              )}
                              {plan.documentUrl && (
                                <Box mt={0.5}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<DownloadIcon fontSize="small" />}
                                    onClick={() => window.open(plan.documentUrl, '_blank')}
                                    sx={{
                                      borderColor: THEME_COLORS.primary,
                                      color: THEME_COLORS.primary,
                                      '&:hover': {
                                        borderColor: '#1e1c4e',
                                        backgroundColor: 'rgba(39, 35, 97, 0.04)'
                                      }
                                    }}
                                  >
                                    Download Document
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    </Paper>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Subject List */}
        <Grid item sx={{ width: { xs: '100%', md: '33%' } }}>
          <Card elevation={2} sx={{ borderRadius: 2, maxHeight: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                My Subjects
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {filteredSubjects.length === 0 ? (
                <Box py={4} textAlign="center">
                  <Typography color="textSecondary" mb={2}>
                    {searchTerm || phaseFilter ? 
                      'No subjects match your filters' : 
                      'No subjects found'}
                  </Typography>
                  {!searchTerm && !phaseFilter && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddSubjectDialogOpen}
                      sx={{
                        bgcolor: THEME_COLORS.primary,
                        '&:hover': {
                          bgcolor: '#1e1c4e'
                        }
                      }}
                    >
                      Add Your First Subject
                    </Button>
                  )}
                </Box>
              ) : (
                <List disablePadding>
                  {filteredSubjects.map((subject) => (
                    <ListItem 
                      key={subject.id} 
                      disablePadding
                      sx={{ mb: 1 }}
                      secondaryAction={
                        <IconButton 
                          edge="end" 
                          onClick={() => handleEditSubjectDialogOpen(subject)}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <Button
                        fullWidth
                        variant={selectedSubject?.id === subject.id ? "contained" : "outlined"}
                        onClick={() => handleSubjectChange(subject)}
                        sx={{
                          justifyContent: "flex-start",
                          textAlign: "left",
                          textTransform: "none",
                          pl: 2,
                          py: 1.5,
                          borderRadius: 1,
                          bgcolor: selectedSubject?.id === subject.id ? THEME_COLORS.primary : 'transparent',
                          color: selectedSubject?.id === subject.id ? 'white' : 'text.primary',
                          borderColor: selectedSubject?.id === subject.id ? THEME_COLORS.primary : 'divider',
                          '&:hover': {
                            bgcolor: selectedSubject?.id === subject.id ? '#1e1c4e' : 'rgba(39, 35, 97, 0.04)',
                            borderColor: selectedSubject?.id === subject.id ? '#1e1c4e' : THEME_COLORS.primary,
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" width="100%" pr={4}>
                          <DescriptionIcon sx={{ 
                            color: selectedSubject?.id === subject.id ? 'white' : THEME_COLORS.primary,
                            mr: 1.5
                          }} />
                          <Box>
                            <Typography 
                              variant="body2" 
                              fontWeight="medium"
                              color="inherit"
                            >
                              {subject.name}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color={selectedSubject?.id === subject.id ? 'inherit' : 'text.secondary'}
                            >
                              {subject.code ? `${subject.code} • ` : ''}Grade {subject.grade}
                            </Typography>
                          </Box>
                        </Box>
                      </Button>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Menu for Plan Options */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => {
          const plan = subjectPlans.find(p => p.id === menuPlanId);
          if (plan) handleEditPlanDialogOpen(plan);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Plan</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleUploadDialogOpen(menuPlanId)}>
          <ListItemIcon>
            <UploadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Upload Document</ListItemText>
        </MenuItem>
        {subjectPlans.find(p => p.id === menuPlanId)?.documentUrl && (
          <MenuItem onClick={() => {
            const plan = subjectPlans.find(p => p.id === menuPlanId);
            if (plan?.documentUrl) window.open(plan.documentUrl, '_blank');
            handleMenuClose();
          }}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download Document</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleDeletePlan(menuPlanId)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: '#f44336' }} />
          </ListItemIcon>
          <ListItemText sx={{ color: '#f44336' }}>Delete Plan</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add Subject Dialog */}
      <Dialog
        open={isAddSubjectDialogOpen}
        onClose={handleAddSubjectDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Subject</DialogTitle>
      <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} sx={{ minWidth: 240 }}>
              <TextField
                autoFocus
                margin="dense"
                name="name"
                label="Subject Name"
                type="text"
                fullWidth
                value={newSubjectData.name}
                onChange={handleNewSubjectChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 240 }}>
              <TextField
                margin="dense"
                name="code"
                label="Subject Code"
                type="text"
                fullWidth
                value={newSubjectData.code}
                onChange={handleNewSubjectChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 240 }}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Phase</InputLabel>
                <Select
                  name="phase"
                  value={newSubjectData.phase}
                  label="Phase"
                  onChange={handleNewSubjectChange}
                >
                  <MenuItem value="Foundation">Foundation Phase</MenuItem>
                  <MenuItem value="Intermediate">Intermediate Phase</MenuItem>
                  <MenuItem value="Senior">Senior Phase</MenuItem>
                  <MenuItem value="FET">FET Phase</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 240 }}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Grade</InputLabel>
                <Select
                  name="grade"
                  value={newSubjectData.grade}
                  label="Grade"
                  onChange={handleNewSubjectChange}
                >
                  <MenuItem value="R">Grade R</MenuItem>
                  <MenuItem value="1">Grade 1</MenuItem>
                  <MenuItem value="2">Grade 2</MenuItem>
                  <MenuItem value="3">Grade 3</MenuItem>
                  <MenuItem value="4">Grade 4</MenuItem>
                  <MenuItem value="5">Grade 5</MenuItem>
                  <MenuItem value="6">Grade 6</MenuItem>
                  <MenuItem value="7">Grade 7</MenuItem>
                  <MenuItem value="8">Grade 8</MenuItem>
                  <MenuItem value="9">Grade 9</MenuItem>
                  <MenuItem value="10">Grade 10</MenuItem>
                  <MenuItem value="11">Grade 11</MenuItem>
                  <MenuItem value="12">Grade 12</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ minWidth: 240 }}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Department</InputLabel>
                <Select
                  name="department"
                  value={newSubjectData.department}
                  label="Department"
                  onChange={handleNewSubjectChange}
                >
                  <MenuItem value="Mathematics">Mathematics</MenuItem>
                  <MenuItem value="Sciences">Sciences</MenuItem>
                  <MenuItem value="Languages">Languages</MenuItem>
                  <MenuItem value="Commerce">Commerce</MenuItem>
                  <MenuItem value="Social Sciences">Social Sciences</MenuItem>
                  <MenuItem value="Arts">Arts</MenuItem>
                  <MenuItem value="Technology">Technology</MenuItem>
                  <MenuItem value="Life Skills">Life Skills</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ minWidth: 240 }}>
              <TextField
                margin="dense"
                name="description"
                label="Description (Optional)"
                type="text"
                fullWidth
                multiline
                rows={3}
                value={newSubjectData.description}
                onChange={handleNewSubjectChange}
              />
            </Grid>
            <Grid item xs={12} sx={{ minWidth: 240 }}>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" component="div" gutterBottom>
                  Policy Document
                </Typography>
                {newSubjectData.policyDocument ? (
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <DescriptionIcon sx={{ color: THEME_COLORS.primary }} />
                      <Typography variant="body2" component="span">
                        {newSubjectData.policyDocument.name}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => setNewSubjectData(prev => ({ ...prev, policyDocument: null }))}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                ) : (
                  <Box>
                    <input
                      accept=".pdf,.doc,.docx"
                      style={{ display: 'none' }}
                      id="policy-document-upload"
                      type="file"
                      onChange={handlePolicyDocumentChange}
                    />
                    <label htmlFor="policy-document-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                        sx={{
                          borderColor: THEME_COLORS.primary,
                          color: THEME_COLORS.primary,
                          '&:hover': {
                            borderColor: THEME_COLORS.primary,
                            backgroundColor: 'rgba(39, 35, 97, 0.04)'
                          }
                        }}
                      >
                        Upload Policy Document
                      </Button>
                    </label>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddSubjectDialogClose}>Cancel</Button>
          <Button 
            onClick={handleAddSubject} 
            variant="contained"
            sx={{
              bgcolor: THEME_COLORS.primary,
              '&:hover': {
                bgcolor: '#1e1c4e'
              }
            }}
          >
            Add Subject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Subject Dialog */}
      <Dialog
        open={isEditSubjectDialogOpen}
        onClose={handleEditSubjectDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Subject</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} sx={{ minWidth: 240 }}>
              <TextField
                autoFocus
                margin="dense"
                name="name"
                label="Subject Name"
                type="text"
                fullWidth
                value={editSubjectData.name}
                onChange={handleEditSubjectChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 240 }}>
              <TextField
                margin="dense"
                name="code"
                label="Subject Code"
                type="text"
                fullWidth
                value={editSubjectData.code}
                onChange={handleEditSubjectChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 240 }}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Phase</InputLabel>
                <Select
                  name="phase"
                  value={editSubjectData.phase}
                  label="Phase"
                  onChange={handleEditSubjectChange}
                >
                  <MenuItem value="Foundation">Foundation Phase</MenuItem>
                  <MenuItem value="Intermediate">Intermediate Phase</MenuItem>
                  <MenuItem value="Senior">Senior Phase</MenuItem>
                  <MenuItem value="FET">FET Phase</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 240 }}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Grade</InputLabel>
                <Select
                  name="grade"
                  value={editSubjectData.grade}
                  label="Grade"
                  onChange={handleEditSubjectChange}
                >
                  <MenuItem value="R">Grade R</MenuItem>
                  <MenuItem value="1">Grade 1</MenuItem>
                  <MenuItem value="2">Grade 2</MenuItem>
                  <MenuItem value="3">Grade 3</MenuItem>
                  <MenuItem value="4">Grade 4</MenuItem>
                  <MenuItem value="5">Grade 5</MenuItem>
                  <MenuItem value="6">Grade 6</MenuItem>
                  <MenuItem value="7">Grade 7</MenuItem>
                  <MenuItem value="8">Grade 8</MenuItem>
                  <MenuItem value="9">Grade 9</MenuItem>
                  <MenuItem value="10">Grade 10</MenuItem>
                  <MenuItem value="11">Grade 11</MenuItem>
                  <MenuItem value="12">Grade 12</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ minWidth: 240 }}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Department</InputLabel>
                <Select
                  name="department"
                  value={editSubjectData.department}
                  label="Department"
                  onChange={handleEditSubjectChange}
                >
                  <MenuItem value="Mathematics">Mathematics</MenuItem>
                  <MenuItem value="Sciences">Sciences</MenuItem>
                  <MenuItem value="Languages">Languages</MenuItem>
                  <MenuItem value="Commerce">Commerce</MenuItem>
                  <MenuItem value="Social Sciences">Social Sciences</MenuItem>
                  <MenuItem value="Arts">Arts</MenuItem>
                  <MenuItem value="Technology">Technology</MenuItem>
                  <MenuItem value="Life Skills">Life Skills</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ minWidth: 240 }}>
              <TextField
                margin="dense"
                name="description"
                label="Description (Optional)"
                type="text"
                fullWidth
                multiline
                rows={3}
                value={editSubjectData.description}
                onChange={handleEditSubjectChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => handleDeleteSubject(editSubjectData.id)} 
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete Subject
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={handleEditSubjectDialogClose}>Cancel</Button>
          <Button 
            onClick={handleUpdateSubject} 
            variant="contained"
            sx={{
              bgcolor: THEME_COLORS.primary,
              '&:hover': {
                bgcolor: '#1e1c4e'
              }
            }}
          >
            Update Subject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Plan Dialog */}
      <Dialog
        open={isAddPlanDialogOpen}
        onClose={handleAddPlanDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Plan</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Plan Name"
            type="text"
            fullWidth
            value={newPlanData.name}
            onChange={handleNewPlanChange}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel>Plan Type</InputLabel>
            <Select
              name="type"
              value={newPlanData.type}
              label="Plan Type"
              onChange={handleNewPlanChange}
            >
              <MenuItem value="sip">Subject Improvement Plan (SIP)</MenuItem>
              <MenuItem value="atp">Annual Teaching Plan (ATP)</MenuItem>
              <MenuItem value="poa">Programme of Assessment (PoA)</MenuItem>
              <MenuItem value="lessonPlan">Lesson Plan</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            name="description"
            label="Description (Optional)"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={newPlanData.description}
            onChange={handleNewPlanChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="dueDate"
            label="Due Date (Optional)"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={newPlanData.dueDate}
            onChange={handleNewPlanChange}
          />
          <Box sx={{ mt: 2 }}>
            <input
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              style={{ display: 'none' }}
              id="new-plan-documents"
              type="file"
              multiple
              onChange={handleNewPlanFilesChange}
            />
            <label htmlFor="new-plan-documents">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                sx={{
                  borderColor: THEME_COLORS.primary,
                  color: THEME_COLORS.primary,
                  '&:hover': {
                    borderColor: THEME_COLORS.primary,
                    bgcolor: 'rgba(39, 35, 97, 0.04)'
                  }
                }}
              >
                Add Documents
              </Button>
            </label>
            {newPlanData.documents.length > 0 && (
              <Box mt={2} display="flex" flexDirection="column" gap={1}>
                {newPlanData.documents.map((doc, index) => (
                  <Paper
                    key={`${doc.name}-${index}`}
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <DescriptionIcon sx={{ color: THEME_COLORS.primary }} />
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {doc.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {doc.size ? `${(doc.size / 1024).toFixed(2)} KB` : 'Unknown size'}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton size="small" onClick={() => handleRemoveNewPlanDoc(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddPlanDialogClose}>Cancel</Button>
          <Button 
            onClick={handleAddPlan} 
            variant="contained"
            sx={{
              bgcolor: THEME_COLORS.primary,
              '&:hover': {
                bgcolor: '#1e1c4e'
              }
            }}
          >
            Add Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Plan Dialog */}
      <Dialog
        open={isEditPlanDialogOpen}
        onClose={handleEditPlanDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Plan</DialogTitle>
      <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Plan Name"
            type="text"
            fullWidth
            value={editPlanData.name}
            onChange={handleEditPlanChange}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel>Plan Type</InputLabel>
            <Select
              name="type"
              value={editPlanData.type}
              label="Plan Type"
              onChange={handleEditPlanChange}
            >
              <MenuItem value="sip">Subject Improvement Plan (SIP)</MenuItem>
              <MenuItem value="atp">Annual Teaching Plan (ATP)</MenuItem>
              <MenuItem value="poa">Programme of Assessment (PoA)</MenuItem>
              <MenuItem value="lessonPlan">Lesson Plan</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={editPlanData.status}
              label="Status"
              onChange={handleEditPlanChange}
            >
              <MenuItem value="not-started">Not Started</MenuItem>
              <MenuItem value="in-progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            name="description"
            label="Description (Optional)"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={editPlanData.description}
            onChange={handleEditPlanChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="dueDate"
            label="Due Date (Optional)"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={editPlanData.dueDate}
            onChange={handleEditPlanChange}
          />
          <Box sx={{ mt: 2 }}>
            <input
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              style={{ display: 'none' }}
              id="edit-plan-documents"
              type="file"
              multiple
              onChange={handleEditPlanFilesChange}
            />
            <label htmlFor="edit-plan-documents">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                sx={{
                  borderColor: THEME_COLORS.primary,
                  color: THEME_COLORS.primary,
                  '&:hover': {
                    borderColor: THEME_COLORS.primary,
                    bgcolor: 'rgba(39, 35, 97, 0.04)'
                  }
                }}
              >
                Add Documents
              </Button>
            </label>
            {editPlanData.documents.length > 0 && (
              <Box mt={2} display="flex" flexDirection="column" gap={1}>
                {editPlanData.documents.map((doc, index) => (
                  <Paper
                    key={`${doc.name}-${index}`}
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <DescriptionIcon sx={{ color: THEME_COLORS.primary }} />
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {doc.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {doc.size ? `${(doc.size / 1024).toFixed(2)} KB` : 'Unknown size'}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton size="small" onClick={() => handleRemoveEditPlanDoc(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditPlanDialogClose}>Cancel</Button>
          <Button 
            onClick={handleUpdatePlan} 
            variant="contained"
            sx={{
              bgcolor: THEME_COLORS.primary,
              '&:hover': {
                bgcolor: '#1e1c4e'
              }
            }}
          >
            Update Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog
        open={isUploadDialogOpen}
        onClose={handleUploadDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ my: 2 }}>
            <input
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              style={{ display: 'none' }}
              id="document-file"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="document-file">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                sx={{
                  borderColor: THEME_COLORS.primary,
                  color: THEME_COLORS.primary,
                  '&:hover': {
                    borderColor: THEME_COLORS.primary,
                    bgcolor: 'rgba(39, 35, 97, 0.04)'
                  }
                }}
              >
                Choose File
              </Button>
            </label>
            {selectedFile && (
              <Box mt={2}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 1, display: 'flex', alignItems: 'center' }}
                >
                  <DescriptionIcon sx={{ color: THEME_COLORS.primary, mr: 1 }} />
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadDialogClose}>Cancel</Button>
          <Button 
            onClick={handleUploadDocument} 
            variant="contained"
            disabled={!selectedFile}
            sx={{
              bgcolor: THEME_COLORS.primary,
              '&:hover': {
                bgcolor: '#1e1c4e'
              }
            }}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubjectPlanning;
